import type { LegalDoc } from '../types';

// Transcribed 1:1 from "03 - Politica de Articulos Prohibidos.pdf" (MEC Vault/legal/app-legal).
export const articulosProhibidos: LegalDoc = {
  slug: '/articulos-prohibidos',
  title: 'Política de Artículos Prohibidos',
  version: '1.0',
  updated: 'Julio de 2026',
  entity: 'Mi Envío Cuba',
  seoDescription:
    'Artículos que no pueden procesarse a través de la plataforma Mi Envío Cuba: categorías prohibidas por la ley de EE. UU., la ley cubana y por razones operativas.',
  closing: 'Fin de la Política de Artículos Prohibidos.',
  sections: [
    {
      id: 'seccion-1',
      number: '1',
      title: 'Introducción',
      blocks: [
        {
          t: 'p',
          text: 'Los siguientes artículos **no pueden procesarse a través de la plataforma Mi Envío Cuba**, ya sea mediante envíos a Cuba, pedidos en el marketplace, pre-avisos de Casillero Envíos, o Compras Asistidas de Casillero Tienda. Esta política aplica a todos los servicios que la Plataforma facilita.',
        },
        {
          t: 'p',
          text: 'Mi Envío Cuba es una **plataforma de software**. El movimiento físico de los bienes lo realizan las agencias de envío, los comerciantes cubanos, los transportistas externos y los servicios de las tiendas externas — cada uno de ellos, actores independientes de MEC. Esta política define los artículos que:',
        },
        {
          t: 'ul',
          items: [
            '**No pueden ser objeto de una transacción registrada en la Plataforma**, con base en la ley de EE. UU., la ley cubana, y las decisiones de MEC como operador de la Plataforma.',
            'No serán aceptados por las Agencias, Comerciantes ni transportistas asociados que operan en el corredor a través de la Plataforma.',
          ],
        },
        {
          t: 'p',
          text: 'Los artículos prohibidos incluyen tanto los prohibidos por la ley de los Estados Unidos (regulaciones OFAC de sanciones cubanas, regulaciones de exportación de EE. UU. — EAR/BIS, y ley federal en general), como los prohibidos por la ley cubana (aduana, importación y ley general de Cuba), y aquellos que MEC ha determinado que la Plataforma no procesará por razones operativas o de seguridad.',
        },
        {
          t: 'p',
          text: 'Es responsabilidad del Usuario verificar que los artículos que envía, ordena o compra a través de la Plataforma no estén incluidos en esta lista. MEC, las Agencias, los Comerciantes y los transportistas se reservan cada uno el derecho, en el ámbito de su propia operación, de rechazar, retener o entregar a las autoridades cualquier artículo prohibido.',
        },
        {
          t: 'p',
          text: 'Adicionalmente, cada Agencia y cada Comerciante puede establecer **restricciones adicionales propias** (por ejemplo, límites de peso o dimensión, categorías de producto que no ofrece, plazos de entrega para ciertos artículos). Cuando corresponda, esas restricciones se muestran en la Aplicación al momento de la transacción.',
        },
      ],
    },
    {
      id: 'seccion-2',
      number: '2',
      title: 'Categorías prohibidas absolutamente',
      blocks: [
        { t: 'h3', text: '2.1 Armas, munición y explosivos' },
        {
          t: 'ul',
          items: [
            'Armas de fuego de cualquier tipo (pistolas, escopetas, rifles, armas antiguas o de colección).',
            'Munición, cartuchos, casquillos, pólvora.',
            'Partes, piezas o accesorios de armas de fuego (miras telescópicas, cargadores, silenciadores, culatas, cañones).',
            'Armas blancas de uso restringido (cuchillos con hoja mayor a 4 pulgadas, machetes de combate, katanas, dagas de asalto).',
            'Armas de electrochoque (tasers), sprays de defensa personal en dosis restringidas.',
            'Explosivos, detonadores, mechas, dinamita, fuegos artificiales, pirotecnia.',
            'Réplicas realistas de armas (airsoft, paintball con apariencia de arma real).',
          ],
        },
        { t: 'h3', text: '2.2 Drogas y sustancias controladas' },
        {
          t: 'ul',
          items: [
            'Drogas ilícitas de cualquier tipo (marihuana, cocaína, opiáceos, metanfetaminas, MDMA, LSD, hongos, etc.), aunque sean legales en el estado o país de origen.',
            'Precursores químicos para la fabricación de drogas.',
            'Parafernalia asociada al consumo de drogas (pipas, papeles, molinillos, escalas).',
            'Cannabis y productos derivados de cannabis (CBD, THC), incluso los legales bajo ley estatal de EE. UU.',
            'Kratom, kava, y otras sustancias psicoactivas no reguladas o de estatus legal ambiguo.',
            'Esteroides anabólicos, hormonas de crecimiento humano (HGH), y otras sustancias de la Ley de Sustancias Controladas.',
          ],
        },
        { t: 'h3', text: '2.3 Medicamentos' },
        {
          t: 'ul',
          items: [
            'Medicamentos con receta (Rx), incluyendo antibióticos, opioides, ansiolíticos, antidepresivos, y cualquier medicamento que requiera prescripción médica en EE. UU. o en Cuba.',
            'Medicamentos falsificados o de origen no verificable.',
            'Medicamentos con fecha de caducidad vencida.',
            'Medicamentos que requieren cadena de frío (refrigeración continua) fuera del canal aprobado.',
            'Vacunas, hemoderivados, tejidos biológicos.',
          ],
        },
        {
          t: 'note',
          text: '**Excepción posible pendiente de definición regulatoria:** medicamentos de venta libre (OTC) para uso personal en cantidades limitadas. Consultar la sección de FAQ o comunicarse con MEC antes de enviar.',
        },
        { t: 'h3', text: '2.4 Bienes controlados por exportación de EE. UU. (EAR)' },
        {
          t: 'ul',
          items: [
            'Bienes clasificados en la lista de control de comercio (CCL) de EE. UU. sin la licencia correspondiente.',
            'Equipos de comunicación militar o de doble uso.',
            'Equipos de cifrado avanzado sujetos a control de exportación.',
            'Tecnología o software sujeto a control de exportación.',
            'Equipos de visión nocturna, láseres militares, dispositivos de contramedidas.',
            'Equipos con capacidad de vigilancia o intercepción (según clasificación CCL).',
          ],
        },
        { t: 'h3', text: '2.5 Bienes prohibidos por sanciones OFAC' },
        {
          t: 'ul',
          items: [
            'Bienes destinados a personas sancionadas por OFAC.',
            'Bienes destinados a funcionarios prohibidos del gobierno cubano (31 C.F.R. § 515.337) o miembros prohibidos del Partido Comunista de Cuba (31 C.F.R. § 515.338).',
            'Bienes destinados al gobierno cubano en su capacidad oficial.',
            'Bienes con propósito comercial en Cuba fuera de las excepciones aplicables.',
          ],
        },
        { t: 'h3', text: '2.6 Bienes prohibidos por la ley cubana' },
        {
          t: 'ul',
          items: [
            'Publicaciones, materiales impresos o audiovisuales que la ley cubana clasifique como subversivos o contrarios al orden constitucional.',
            'Materiales pornográficos.',
            'Equipos de telecomunicaciones no autorizados por la ley cubana (radios profesionales, transmisores, ciertos tipos de antenas, drones sin autorización).',
            'Bienes de importación restringida bajo el Arancel de Aduana de Cuba (verificar categorías vigentes).',
          ],
        },
      ],
    },
    {
      id: 'seccion-3',
      number: '3',
      title: 'Categorías que no se procesan a través de la Plataforma',
      blocks: [
        {
          t: 'p',
          text: 'Los siguientes bienes, aunque puedan ser legales en algunas circunstancias, **no se procesan a través de la Plataforma** por razones operativas, de seguridad o de riesgo:',
        },
        { t: 'h3', text: '3.1 Perecederos sin cadena de frío autorizada' },
        {
          t: 'ul',
          items: [
            'Carnes, mariscos, quesos frescos, y otros alimentos que requieran refrigeración continua.',
            'Productos horneados con fecha de vencimiento corta.',
            'Frutas y verduras frescas (excepto en programas específicos con transitarias autorizadas).',
          ],
        },
        {
          t: 'p',
          text: 'Los alimentos no perecederos (conservas, secos, granos, café, chocolate en tabletas, etc.) sí son aceptados dentro de las categorías estándar de envíos.',
        },
        { t: 'h3', text: '3.2 Animales vivos, plantas vivas y productos derivados' },
        {
          t: 'ul',
          items: [
            'Mascotas y cualquier animal vivo.',
            'Plantas vivas, semillas viables, esquejes.',
            'Marfil, cuernos, pieles exóticas, y cualquier producto derivado de especies protegidas por CITES.',
          ],
        },
        { t: 'h3', text: '3.3 Efectivo, instrumentos monetarios, metales preciosos' },
        {
          t: 'ul',
          items: [
            'Dinero en efectivo (cualquier moneda) en cantidades superiores a las permitidas por la ley aduanal cubana.',
            'Cheques al portador, giros postales, tarjetas prepagadas con saldo.',
            'Oro, plata y otros metales preciosos en forma de barras, monedas o joyería de alto valor sin declaración correspondiente.',
            'Piedras preciosas y semipreciosas de alto valor.',
          ],
        },
        { t: 'h3', text: '3.4 Objetos de valor sustancial sin seguro' },
        {
          t: 'ul',
          items: [
            'Electrónica de valor superior a los límites publicados por la Agencia o el Comerciante (celulares, computadoras, tabletas — verificar los límites vigentes por tipo de envío).',
            'Joyería fina, relojes de lujo.',
            'Obras de arte de alto valor, antigüedades.',
            'Instrumentos musicales profesionales de alto valor.',
          ],
        },
        {
          t: 'p',
          text: 'Estos objetos pueden ser enviados en algunos casos **previa aprobación de la Agencia**, con declaración adecuada de valor y arreglo de seguro cuando esté disponible.',
        },
        { t: 'h3', text: '3.5 Sustancias peligrosas (HAZMAT)' },
        {
          t: 'ul',
          items: [
            'Baterías de litio sueltas o dañadas fuera de dispositivo (baterías dentro de un dispositivo son aceptadas en cantidades razonables).',
            'Líquidos inflamables (perfumes con alto contenido de alcohol en grandes cantidades, aerosoles bajo presión, solventes).',
            'Productos químicos industriales o de laboratorio.',
            'Baterías de plomo-ácido.',
            'Materiales radioactivos.',
          ],
        },
        { t: 'h3', text: '3.6 Contenido inapropiado o ilegal' },
        {
          t: 'ul',
          items: [
            'Materiales pornográficos.',
            'Materiales que promuevan el odio, la discriminación o la violencia.',
            'Bienes robados, falsificados, o con derechos de autor infringidos.',
            'Objetos culturales o arqueológicos protegidos por ley (patrimonio nacional cubano o estadounidense).',
          ],
        },
      ],
    },
    {
      id: 'seccion-4',
      number: '4',
      title: 'Casillero Tienda — restricciones adicionales',
      blocks: [
        {
          t: 'p',
          text: 'Además de las categorías anteriores, en Casillero Tienda MEC podrá restringir en cualquier momento:',
        },
        {
          t: 'ul',
          items: [
            '**Tiendas Externas específicas** que no pueden usarse a través del servicio.',
            '**Categorías completas de producto** de una Tienda Externa (por ejemplo, la categoría “electrónica” de una tienda X, pero permitir la categoría “ropa” de la misma tienda).',
            '**Productos específicos** que MEC identifique como no elegibles.',
            '**Volumen o valor máximo** por Compra Asistida.',
          ],
        },
        {
          t: 'p',
          text: 'MEC publica el catálogo vigente de Tiendas Externas y categorías elegibles dentro de la Aplicación.',
        },
      ],
    },
    {
      id: 'seccion-5',
      number: '5',
      title: 'Consecuencias de intentar procesar un artículo prohibido a través de la Plataforma',
      blocks: [
        {
          t: 'p',
          text: 'Cuando MEC, una Agencia asociada, un Comerciante, un transportista o una autoridad identifique un artículo prohibido en una transacción a través de la Plataforma:',
        },
        {
          t: 'ol',
          items: [
            '**Bloqueo o rechazo de la transacción** — MEC podrá bloquear la transacción en la Plataforma o marcarla para revisión; la Agencia o el Comerciante podrá rechazar procesar el servicio subyacente. En ambos casos, sin obligación de reembolso de tarifas de servicio ya cobradas.',
            '**Retención o incautación del artículo** — cuando el artículo físico haya salido del ámbito de la Plataforma (por ejemplo, ya despachado por el Usuario a través de un transportista externo en Casillero Envíos), podrá ser retenido por la Agencia, el transportista o las autoridades, devuelto al Usuario a su costo, o entregado a las autoridades competentes.',
            '**Reporte a las autoridades** — cuando corresponda, MEC reportará el incidente a las autoridades competentes según la ley aplicable. Las Agencias, Comerciantes y transportistas también podrán reportar de forma independiente conforme a sus propias obligaciones legales.',
            '**Suspensión o cierre de la cuenta** — MEC podrá suspender o cerrar la cuenta del Usuario responsable, permanentemente si la infracción lo justifica.',
            '**Responsabilidad legal del Usuario** — el Usuario asume la totalidad de las consecuencias legales, incluyendo multas, sanciones, cargos criminales y costos de investigación aduanal, que puedan derivar del intento de procesamiento.',
          ],
        },
        {
          t: 'p',
          text: 'MEC, las Agencias, los Comerciantes y los transportistas no son responsables ante el Usuario por artículos rechazados, retenidos, incautados o destruidos conforme a esta política.',
        },
      ],
    },
    {
      id: 'seccion-6',
      number: '6',
      title: 'Reportes y consultas',
      blocks: [
        {
          t: 'p',
          text: 'Si tiene dudas sobre si un artículo específico está prohibido o restringido antes de enviarlo, ordenarlo o comprarlo, escríbanos a **support@mienviocuba.com** o utilice la sección de Ayuda dentro de la Aplicación.',
        },
        {
          t: 'p',
          text: 'Para reportar un artículo prohibido que ha sido procesado a través de la Plataforma, escriba también a **support@mienviocuba.com**.',
        },
      ],
    },
    {
      id: 'seccion-7',
      number: '7',
      title: 'Modificaciones',
      blocks: [
        {
          t: 'p',
          text: 'MEC puede modificar esta política en cualquier momento sin aviso previo cuando la modificación sea necesaria por cambios en la ley aplicable, cambios en las políticas de Agencias, Comerciantes o transportistas asociados, o por razones operativas. Las modificaciones no materiales se publican dentro de la Aplicación. Las modificaciones materiales se notifican también por correo electrónico.',
        },
        {
          t: 'p',
          text: 'Esta política forma parte integrante de los Términos de Servicio (v1.0) y se rige por el mismo derecho aplicable y foro descrito en aquellos.',
        },
      ],
    },
  ],
};
